import { usePlayer, usePlayerStore } from "@tezzro/player";
import { useAppDispatch, useAppStore } from "@/app/appStore";
import { useCallback } from "react";
import { updateIsPlaybackLoaded } from "@/entities/annotation";

/**
 * Unified player hook that wraps @tezzro/player with app-specific Redux integration.
 * Provides higher-level player actions that coordinate with the application state.
 *
 * Used by both annotation and gondola-change-groups features.
 */
export const usePlayerActions = () => {
  const player = usePlayer();
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const { getState } = usePlayerStore();

  /**
   * Start video playback (VOD or Live mode)
   */
  const startVideo = useCallback(
    (params: {
      cameraId: string;
      seekValueUTC: number;
      isLive?: boolean;
      isPlaying?: boolean;
    }) => {
      const {
        annotation: { dateRange },
      } = store.getState();

      if (!dateRange.start || !dateRange.end) return;

      dispatch(updateIsPlaybackLoaded(false));

      const fromTime = dateRange.start.toISOString();
      const toTime = dateRange.end.toISOString();

      if (params.isLive) {
        player.startLive({
          cameraId: params.cameraId,
          seekValueUTC: params.seekValueUTC,
          autoPlay: params.isPlaying !== false,
        });
      } else {
        player.startVOD({
          cameraId: params.cameraId,
          fromTime,
          toTime,
          seekValueUTC: params.seekValueUTC,
        });
      }
    },
    [player, dispatch, store]
  );

  /**
   * Update video time range (re-initializes player with new range)
   */
  const updateVideoTimeRange = useCallback(
    (params: { start: Date; end: Date; isLive: boolean }) => {
      const {
        annotation: { currentCamera },
      } = store.getState();

      const fromTime = params.start.toISOString();
      const toTime = params.end.toISOString();
      const currentTime = getState().currentTime;

      if (params.isLive) {
        player.startLive({
          cameraId: currentCamera,
          seekValueUTC: currentTime,
        });
      } else {
        player.startVOD({
          cameraId: currentCamera,
          fromTime,
          toTime,
          seekValueUTC: currentTime,
        });
      }
    },
    [player, store, getState]
  );

  return {
    startVideo,
    updateVideoTimeRange,
  };
};
